/**
 * StudentStageGuard
 * -----------------
 * Bug fix 2026-05-24: chặn truy cập nhanh (gõ URL trực tiếp) cho student chưa
 * đủ stage. Cùng pattern với [[CourtroomAccessGuard]] nhưng generic theo stage.
 *
 * Stage levels (tham chiếu Layout.tsx):
 *  - r0 = team chưa duyệt / chưa thuộc đội
 *  - r1 = team status active|auto_approved (đã đăng ký xong)
 *  - r2 = team.competition_status === 'advanced_to_semifinal'
 *  - r3 = team.competition_status === 'advanced_to_final' (Phase 5 2026-05-25)
 *
 * Logic:
 *  - role != student → pass thẳng
 *  - student stage >= required → pass
 *  - else → render Access Denied UI với message tùy stage
 *
 * Phase 5 (2026-05-25): thêm stage r3 cho đội vào Vòng Chung kết. Đảm bảo
 * student R3 vẫn xem được tính năng R1+/R2+ (kế thừa). r2/r3 dùng cùng nhánh
 * UI hiện tại; r3 chỉ mở thêm shortcut Vòng Chung kết nếu có.
 */
import { Link as RouterLink } from 'react-router-dom'
import { Box, Container, Paper, Typography, Button, Alert } from '@mui/material'
import { Block, Home } from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery } from '../store/api/teamsApi'

type Stage = 'r1' | 'r2' | 'r3'

const STAGE_RANK: Record<Stage | 'r0', number> = { r0: 0, r1: 1, r2: 2, r3: 3 }

interface Props {
  children: React.ReactNode
  required: Stage
  /** Optional friendly name của tính năng để hiển thị trong message */
  featureName?: string
}

export default function StudentStageGuard({ children, required, featureName }: Props) {
  const { user } = useAppSelector((s) => s.auth)
  const isStudent = user?.role === 'student'
  const { data: teams = [], isLoading } = useGetTeamsQuery(undefined, { skip: !isStudent })

  if (!isStudent) return <>{children}</>

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography color="text.secondary">Đang kiểm tra quyền truy cập...</Typography>
      </Container>
    )
  }

  const team = teams.find((t) => t.members?.includes(user!.id))
  const status = (team as any)?.status
  const compStatus = (team as any)?.competition_status
  const stage: Stage | 'r0' = !team
    ? 'r0'
    : status !== 'active' && status !== 'auto_approved'
    ? 'r0'
    : compStatus === 'advanced_to_final'
    ? 'r3'
    : compStatus === 'advanced_to_semifinal'
    ? 'r2'
    : 'r1'

  if (STAGE_RANK[stage] >= STAGE_RANK[required]) return <>{children}</>

  const feature = featureName || 'tính năng này'
  let reason = `${feature} chỉ mở cho đội đã được duyệt đăng ký.`
  if (required === 'r3') {
    if (compStatus === 'eliminated')
      reason = `Đội của bạn không thuộc danh sách vào Vòng Chung kết — không đủ điều kiện sử dụng ${feature}.`
    else if (compStatus === 'advanced_to_semifinal')
      reason = `${feature.charAt(0).toUpperCase() + feature.slice(1)} chỉ mở khi đội bạn thắng cặp Bán kết. Vui lòng đợi kết quả cặp đấu được công bố.`
    else
      reason = `${feature.charAt(0).toUpperCase() + feature.slice(1)} chỉ mở cho 2 đội vào Vòng Chung kết.`
  } else if (required === 'r2') {
    if (compStatus === 'eliminated')
      reason = `Đội của bạn đã bị loại sau Vòng Sơ loại — không đủ điều kiện sử dụng ${feature}.`
    else if (!compStatus)
      reason = `Kết quả Vòng Sơ loại chưa được công bố. ${feature.charAt(0).toUpperCase() + feature.slice(1)} sẽ mở khi đội bạn vào top 4 Vòng Bán kết.`
    else reason = `${feature.charAt(0).toUpperCase() + feature.slice(1)} chỉ mở cho các đội đã vào Vòng Bán kết (top 4 sau Sơ loại).`
  } else if (!team) {
    reason = `Bạn chưa thuộc đội thi nào — không thể truy cập ${feature}.`
  } else if (status !== 'active' && status !== 'auto_approved') {
    reason = `Đội của bạn (trạng thái '${status || 'chưa rõ'}') chưa được Ban tổ chức duyệt — không thể truy cập ${feature}.`
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper
        elevation={0}
        sx={(t) => ({
          p: { xs: 3, md: 5 },
          textAlign: 'center',
          borderRadius: 3,
          border: `1px solid ${t.palette.divider}`,
        })}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={(t) => ({
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: t.palette.warning.light,
              color: t.palette.warning.contrastText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <Block sx={{ fontSize: 40 }} />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Chưa thể truy cập
        </Typography>
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
          {reason}
        </Alert>
        <Button
          component={RouterLink}
          to="/dashboard"
          variant="contained"
          startIcon={<Home />}
          sx={{ mt: 3, textTransform: 'none' }}
        >
          Về Trang chủ
        </Button>
      </Paper>
    </Container>
  )
}
